import type { RetroPhase } from './phases.js';
import type { RetroSettings } from './settings.js';

export const TEAM_ROLES = ['FACILITATOR', 'CONTRIBUTOR'] as const;
export type TeamRole = (typeof TEAM_ROLES)[number];

export const PARTICIPANT_KINDS = ['MEMBER', 'GUEST'] as const;
export type ParticipantKind = (typeof PARTICIPANT_KINDS)[number];

export const ACTION_STATUSES = ['OPEN', 'IN_PROGRESS', 'DONE'] as const;
export type ActionStatus = (typeof ACTION_STATUSES)[number];

export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  OPEN: 'To do',
  IN_PROGRESS: 'In progress',
  DONE: 'Done',
};

/* ------------------------------------------------------------------ *
 * Vote targets
 *
 * A vote lands either on a standalone comment or on a group of comments.
 * Both are addressed with a single opaque string key so that vote maps can be
 * plain `Record<string, number>` on the wire.
 * ------------------------------------------------------------------ */

export const VOTE_TARGET_TYPES = ['COMMENT', 'GROUP'] as const;
export type VoteTargetType = (typeof VOTE_TARGET_TYPES)[number];

export type VoteTarget = { type: VoteTargetType; id: string };

export function voteTargetKey(target: VoteTarget): string {
  return `${target.type === 'COMMENT' ? 'c' : 'g'}:${target.id}`;
}

export function parseVoteTargetKey(key: string): VoteTarget | null {
  const prefix = key.slice(0, 2);
  const id = key.slice(2);
  if (!id) return null;
  if (prefix === 'c:') return { type: 'COMMENT', id };
  if (prefix === 'g:') return { type: 'GROUP', id };
  return null;
}

/* ------------------------------------------------------------------ *
 * Client-facing views
 *
 * Everything below is what a client is allowed to see. The server builds these
 * per recipient; fields that would leak authorship or individual votes are
 * simply absent rather than blanked out.
 * ------------------------------------------------------------------ */

export type UserView = {
  id: string;
  name: string;
  email: string;
  avatar: string;
  /** Platform administrator, orthogonal to the per-team roles. */
  isAdmin: boolean;
};

/** A user as an administrator sees them, with the fields only they need. */
export type AdminUserView = UserView & {
  createdAt: string;
  /** ISO timestamp when the account was deleted, or null while it is active. */
  deletedAt: string | null;
  teamCount: number;
};

/** A team as an administrator sees them, including ones they are not in. */
export type AdminTeamView = {
  id: string;
  name: string;
  avatar: string;
  createdAt: string;
  memberCount: number;
  retroCount: number;
  facilitators: string[];
  /** Whether the viewing admin is a member, since they may not be. */
  isMember: boolean;
};

export type TeamView = {
  id: string;
  name: string;
  avatar: string;
  role: TeamRole;
  memberCount: number;
};

export type ColumnView = {
  id: string;
  title: string;
  description: string;
  emoji: string;
  order: number;
};

export type ParticipantView = {
  id: string;
  name: string;
  avatar: string;
  role: TeamRole;
  kind: ParticipantKind;
  isOnline: boolean;
  isReady: boolean;
  /** Populated during and after VOTE so the facilitator can see who still has votes left. */
  votesRemaining?: number;
  hasFinishedVoting?: boolean;
};

export type CommentAuthorView = {
  participantId: string;
  name: string;
  avatar: string;
};

export type ReactionView = {
  emoji: string;
  count: number;
  /** Whether the requesting participant reacted with this emoji. */
  mine: boolean;
  /**
   * Who reacted, for the hover attribution. Reacting is a public act — unlike
   * authorship of an anonymous card or an individual vote, both of which stay
   * hidden. The viewer's own name is included; the client renders it as "You".
   */
  names: string[];
};

export type CommentView = {
  id: string;
  columnId: string;
  groupId: string | null;
  text: string;
  gifUrl: string | null;
  order: number;
  /** null when the comment was posted anonymously. */
  author: CommentAuthorView | null;
  isAnonymous: boolean;
  isMine: boolean;
  topicIds: string[];
  reactions: ReactionView[];
  createdAt: string;
};

export type GroupView = {
  id: string;
  columnId: string;
  title: string;
  order: number;
  commentIds: string[];
  reactions: ReactionView[];
};

export type TopicView = {
  id: string;
  name: string;
  color: string;
  isBuiltin: boolean;
};

export type GroupSuggestionView = {
  id: string;
  commentIds: string[];
  title: string;
};

export type VotesView = {
  votesPerParticipant: number;
  /** targetKey -> how many votes *I* placed. */
  mine: Record<string, number>;
  myVotesUsed: number;
  /**
   * targetKey -> total votes from the whole team. Empty until the retro reaches
   * ACTION_PLAN, so voting cannot be influenced by running totals.
   */
  totals: Record<string, number>;
  /** Live progress indicators, safe to show during voting. */
  teamVotesUsed: number;
  teamVotesTotal: number;
  participantsFinished: number;
  participantCount: number;
};

export type TimerView = {
  /** ISO timestamp when the timer runs out. */
  endsAt: string;
  durationSeconds: number;
  isRunning: boolean;
  /** Remaining seconds captured when the timer was paused. */
  pausedRemainingSeconds: number | null;
};

export type ActionItemView = {
  id: string;
  title: string;
  description: string;
  status: ActionStatus;
  dueDate: string | null;
  assignee: { userId: string; name: string; avatar: string } | null;
  topicIds: string[];
  linkedCommentIds: string[];
  retroId: string;
  retroName: string;
  createdAt: string;
  completedAt: string | null;
};

export type RotiCommentView = {
  /** Deliberately identity-free: ROTI feedback is permanently anonymous. */
  score: number;
  comment: string;
};

export type RotiView = {
  /** Whether the requesting participant has submitted their own rating. */
  hasSubmitted: boolean;
  myScore: number | null;
  responseCount: number;
  /** Results are withheld until the viewer has rated, unless they facilitate. */
  resultsVisible: boolean;
  average: number | null;
  distribution: Record<string, number>;
  comments: RotiCommentView[];
};

export type RetroHeaderView = {
  id: string;
  name: string;
  phase: RetroPhase;
  teamId: string;
  teamName: string;
  templateName: string;
  settings: RetroSettings;
  timer: TimerView | null;
  createdAt: string;
  closedAt: string | null;
};

export type MeView = {
  participantId: string;
  role: TeamRole;
  kind: ParticipantKind;
  name: string;
  avatar: string;
  isReady: boolean;
  isFacilitator: boolean;
};

/** Team members an action item can be assigned to. */
export type AssignableMemberView = {
  userId: string;
  name: string;
  avatar: string;
};

/** The complete redacted state of a retro for one recipient. */
export type RetroSnapshot = {
  retro: RetroHeaderView;
  me: MeView;
  columns: ColumnView[];
  participants: ParticipantView[];
  assignableMembers: AssignableMemberView[];
  /** During COMMENTS this contains only the recipient's own comments. */
  comments: CommentView[];
  /** columnId -> number of comments by *other* people. Only sent during COMMENTS. */
  hiddenCommentCounts: Record<string, number>;
  groups: GroupView[];
  topics: TopicView[];
  votes: VotesView;
  actionItems: ActionItemView[];
  /** Unresolved actions carried over from earlier retros, shown as a reminder. */
  carriedOverActions: ActionItemView[];
  suggestions: GroupSuggestionView[];
  roti: RotiView | null;
  readyCount: number;
};
